import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Construct the array of local frame URLs based on the extracted ezgif sequence
const frameCount = 189; // User has exactly 189 frames
const sequenceImages = Array.from({ length: frameCount }, (_, i) => {
    // ezgif format is usually 3 padding zeros: ezgif-frame-001.jpg
    const frameNumber = (i + 1).toString().padStart(3, '0');
    return `/background-frames/ezgif-frame-${frameNumber}.jpg`;
});

const CanvasBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // We get a slightly higher-performing non-alpha context since we draw completely solid frames mostly
    const context = canvas.getContext('2d', { alpha: false }); 
    
    const resizeCanvas = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
    };
    resizeCanvas();

    const images = [];
    let loadedImages = 0;
    
    // Abstract state object that GSAP will scrub
    const scrollObj = { progress: 0 };

    // High performance image drawer 
    const drawImageCover = (img, opacity) => {
        if (!img || !img.complete) return;
        context.globalAlpha = opacity;
        
        const hRatio = canvas.width / img.width;
        const vRatio = canvas.height / img.height;
        const ratio = Math.max(hRatio, vRatio);
        
        const centerShift_x = (canvas.width - img.width * ratio) / 2;
        const centerShift_y = (canvas.height - img.height * ratio) / 2;

        context.drawImage(
            img, 
            0, 0, img.width, img.height,
            centerShift_x, centerShift_y, img.width * ratio, img.height * ratio
        );
    };

    // Draw function invoked on every tick by GSAP
    const render = () => {
        if (loadedImages === 0) return;
        
        // No clearRect needed because we always draw the base image at 1.0 opacity completely covering the canvas
        // This is a massive performance optimization over drawing transparently and clearing rect.

        const totalTransitions = frameCount - 1;
        if (totalTransitions < 1) return;

        const scaledProgress = scrollObj.progress * totalTransitions;
        const index1 = Math.floor(scaledProgress);
        const index2 = Math.min(index1 + 1, totalTransitions);
        const blend = scaledProgress - index1;

        // Draw the main base frame
        if (images[index1]) {
            drawImageCover(images[index1], 1);
        }
        
        // If we are halfway between frames, crossfade the next one on top to make a perfect 60fps illusion
        if (index1 !== index2 && blend > 0 && images[index2]) {
            drawImageCover(images[index2], blend);
        }
        
        context.globalAlpha = 1; // Always reset
    };

    // Lazy load the images, but immediately draw the first one so the screen isn't black
    sequenceImages.forEach((src, idx) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
            loadedImages++;
            if (idx === 0) render(); 
        };
        images.push(img);
    });

    // Tie the scrub to the full height of the document
    gsap.to(scrollObj, {
        progress: 1,
        ease: "none",
        scrollTrigger: {
            trigger: document.body,
            start: "top top",
            end: "bottom bottom",
            scrub: 1.2 // very short scrub latency to make it feel glued to your finger
        },
        onUpdate: render
    });

    // Rerender perfectly on resize
    window.addEventListener('resize', () => {
        resizeCanvas();
        render();
    });

    return () => {
        window.removeEventListener('resize', resizeCanvas);
        ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <canvas 
        ref={canvasRef} 
        // Force hardware acceleration and prevent pointer events
        className="fixed top-0 left-0 w-screen h-screen z-[-5] pointer-events-none opacity-20 max-w-full will-change-transform"
        style={{ transform: 'translateZ(0)' }} 
    />
  );
};

export default CanvasBackground;
